import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CreditCardIcon, CheckIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const MAIN_PRICE = 7500;
export function PricingCard() {
  const [openItem, setOpenItem] = useState<string | null>('package-1');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id);
  };
  return <section id="pricing" className="py-20 md:py-28 px-4 md:px-6 border-b border-white/[0.06]" data-id="element-99">
      <div className="max-w-5xl mx-auto" data-id="element-100">
        <ScrollReveal data-id="element-101">
          <div className="text-center mb-12 md:mb-16" data-id="element-102">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4" data-id="element-103">
              Pricing
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight" data-id="element-104">
              Investment
            </h2>
            <div className="max-w-2xl mx-auto mt-6 md:mt-8 text-left" data-id="element-105">
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed font-light mb-4" data-id="element-106">
                This project is not simply about redesigning a logo or creating
                visual materials.
              </p>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed font-light mb-4" data-id="element-107">
                The purpose of the work is to create clarity for the
                organisation. Clarity about how NPP Neuro Group presents its
                expertise, how it communicates with referral partners, and how
                the business positions itself as a specialist neurological
                rehabilitation provider.
              </p>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed font-light" data-id="element-108">
                When that clarity exists, the brand, messaging and marketing
                materials all become easier to create and easier for the team to
                use.
              </p>
            </div>
          </div>
        </ScrollReveal>

        <div className="space-y-4" data-id="element-109">
          {/* PACKAGE 1: Main Project */}
          <ScrollReveal direction="up" data-id="element-110">
            <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === 'package-1' ? 'border-accent/50 shadow-[0_0_30px_-10px_rgba(255,107,53,0.15)]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-111">
              <button onClick={() => toggleItem('package-1')} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4 relative" data-id="element-112">
                {openItem === 'package-1' && <motion.div layoutId="active-border" className="absolute top-0 left-0 w-1 h-full bg-accent" data-id="element-113" />}
                <div className="flex-1 pl-2 md:pl-0" data-id="element-114">
                  <div className="flex items-center gap-3 mb-2" data-id="element-115">
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight" data-id="element-116">
                      Rebrand Project
                    </h3>
                    <span className="bg-accent text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest rounded-sm" data-id="element-117">
                      Recommended
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm md:text-base font-light" data-id="element-118">
                    A complete brand identity and positioning project to
                    reposition NPP Neuro Group as a trusted specialist
                    neurological rehabilitation provider.
                  </p>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-6 pl-2 md:pl-0 w-full md:w-auto" data-id="element-119">
                  <div className="text-left md:text-right" data-id="element-120">
                    <div className="text-xl md:text-2xl font-black text-white tracking-tight" data-id="element-121">
                      £{MAIN_PRICE.toLocaleString()}{' '}
                      <span className="text-sm font-normal text-zinc-500" data-id="element-122">
                        + VAT
                      </span>
                    </div>
                    <div className="text-zinc-500 font-mono text-xs uppercase tracking-wider" data-id="element-123">
                      8–12 weeks duration
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openItem === 'package-1' ? 'rotate-180 text-accent' : ''}`} data-id="element-124" />
                </div>
              </button>

              <AnimatePresence data-id="element-125">
                {openItem === 'package-1' && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-126">
                    <div className="px-6 md:px-8 pb-8 md:pb-12 border-t border-white/[0.06] pt-8" data-id="element-127">
                      <div className="mb-10 max-w-3xl" data-id="element-128">
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed font-light mb-4" data-id="element-129">
                          Built to move NPP Neuro Group from a brand that
                          reflects the past to one that clearly represents the
                          expertise and direction of the organisation today. Key
                          visual identity direction ready within 6 weeks.
                        </p>
                        <p className="text-zinc-500 text-sm font-mono" data-id="element-130">
                          Typical investment £12,500. Project rate £7,500 + VAT.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 mb-12" data-id="element-131">
                        <div data-id="element-132">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-133">
                            What's included
                          </h4>
                          <ul className="space-y-4" data-id="element-134">
                            {['Discovery deep dive workshop', 'Brand positioning and messaging clarity', 'Exploration of brand structure including possible naming approach', 'Brand identity refresh including logo system, typography, colour palette and visual language', 'Brand guidelines', 'Design of marketing assets including roll up banners, business cards, email signatures, social media templates and conference brochures'].map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-300" data-id="element-135">
                                <CheckIcon className="w-4 h-4 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-136" />
                                {item}
                              </li>)}
                          </ul>
                        </div>

                        <div data-id="element-137">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-138">
                            Investment & Payment Terms
                          </h4>
                          <div className="bg-surface-elevated border border-white/[0.06] p-6 md:p-8 mb-6" data-id="element-139">
                            <div className="text-2xl font-black text-white mb-6" data-id="element-140">
                              £7,500{' '}
                              <span className="text-sm font-normal text-zinc-500" data-id="element-141">
                                + VAT
                              </span>
                            </div>
                            <div className="space-y-5" data-id="element-142">
                              <div className="flex items-start" data-id="element-143">
                                <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mr-4 flex-shrink-0 text-accent font-mono text-xs font-bold" data-id="element-144">
                                  1
                                </div>
                                <div data-id="element-145">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-146">
                                    50% Upfront
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-147">
                                    £3,750 + VAT • Due to secure the project
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-148">
                                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mr-4 flex-shrink-0 text-zinc-400 font-mono text-xs font-bold" data-id="element-149">
                                  2
                                </div>
                                <div data-id="element-150">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-151">
                                    25% Design Approval
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-152">
                                    £1,875 + VAT • Upon approval of chosen
                                    identity direction
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-153">
                                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mr-4 flex-shrink-0 text-zinc-400 font-mono text-xs font-bold" data-id="element-154">
                                  3
                                </div>
                                <div data-id="element-155">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-156">
                                    25% Completion
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-157">
                                    £1,875 + VAT • Upon completion prior to
                                    handover
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="mt-5 text-[10px] text-zinc-600 uppercase tracking-widest border-t border-white/[0.06] pt-4" data-id="element-158">
                              Work begins once initial payment is received.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-159">
                        <button onClick={e => {
                      e.stopPropagation();
                      setShowBankDetails(!showBankDetails);
                    }} className="flex items-center text-xs md:text-sm font-bold text-zinc-400 hover:text-white transition-colors uppercase tracking-widest" data-id="element-160">
                          <CreditCardIcon className="w-4 h-4 mr-3" data-id="element-161" />
                          {showBankDetails ? 'Hide bank details' : 'Show bank details'}
                          <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform ${showBankDetails ? 'rotate-180' : ''}`} data-id="element-162" />
                        </button>

                        <AnimatePresence data-id="element-163">
                          {showBankDetails && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-164">
                              <div className="mt-6 p-6 bg-[#050507] text-white border border-white/[0.06]" data-id="element-165">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm" data-id="element-166">
                                  <div data-id="element-167">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-168">
                                      Bank
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-169">
                                      Clearbank
                                    </span>
                                  </div>
                                  <div data-id="element-170">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-171">
                                      Account name
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-172">
                                      AVY Consultancy Limited
                                    </span>
                                  </div>
                                  <div data-id="element-173">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-174">
                                      Company number
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-175">
                                      12143361
                                    </span>
                                  </div>
                                  <div data-id="element-176">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-177">
                                      Sort code
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-178">
                                      04-06-05
                                    </span>
                                  </div>
                                  <div data-id="element-179">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-180">
                                      Account no
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-181">
                                      16926743
                                    </span>
                                  </div>
                                  <div data-id="element-182">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-183">
                                      Reference
                                    </span>
                                    <span className="font-mono text-accent" data-id="element-184">
                                      NPP-BRAND-2026
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </ScrollReveal>

          {/* ONGOING PACKAGES */}
          {[{
          id: 'package-2',
          title: 'Ongoing Creative Support',
          price: '£750/mo',
          duration: 'Ongoing monthly',
          description: 'Ongoing design support so the team can focus on delivering care. We handle the creative work to keep the brand consistent and professional across all materials.',
          deliverables: ['Up to 2 design days per month', 'Social media graphics', 'Marketing materials', 'Presentation design', 'Brochure updates', 'Short animations for social media', 'Ongoing support']
        }, {
          id: 'package-3',
          title: 'Growth Support',
          price: '£1,500/mo',
          duration: 'Ongoing monthly',
          description: 'Extended creative support with a focus on referral growth content. Designed to strengthen visibility with solicitors, case managers and referral partners.',
          deliverables: ['Up to 3 design days per month plus referral growth content', 'Social media content design including static posts, animation posts, educational content and testimonial content', 'Newsletter design and setup', 'Mailchimp template structure', 'Referral partner branded collateral package', 'Presentation design and updates']
        }, {
          id: 'package-4',
          title: 'Paid Social Campaigns',
          price: '£500/mo',
          duration: 'Optional monthly',
          description: 'Brand awareness through paid social campaigns. The management fee covers campaign setup, creative preparation, optimisation and reporting.',
          deliverables: ['Campaign setup and management', 'Creative preparation for ads', 'Ongoing optimisation', 'Monthly reporting', 'Minimum advertising spend £500 per month paid directly to Meta']
        }].map((pkg, index) => <ScrollReveal key={pkg.id} direction="up" delay={0.1 + index * 0.05} data-id="element-185">
              <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === pkg.id ? 'border-white/[0.12]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-186">
                <button onClick={() => toggleItem(pkg.id)} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4" data-id="element-187">
                  <h3 className={`text-lg md:text-xl font-bold uppercase tracking-tight transition-colors ${openItem === pkg.id ? 'text-white' : 'text-zinc-400'}`} data-id="element-188">
                    {pkg.title}
                  </h3>
                  <div className="flex items-center gap-6" data-id="element-189">
                    <div className="text-left md:text-right" data-id="element-190">
                      <span className="text-lg md:text-xl font-black text-white tracking-tight" data-id="element-191">
                        {pkg.price}
                      </span>
                      <span className="text-sm font-normal text-zinc-500 ml-1" data-id="element-192">
                        + VAT
                      </span>
                      <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider" data-id="element-193">
                        {pkg.duration}
                      </div>
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 flex-shrink-0 ${openItem === pkg.id ? 'rotate-180 text-white' : ''}`} data-id="element-194" />
                  </div>
                </button>
                <AnimatePresence data-id="element-195">
                  {openItem === pkg.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-196">
                      <div className="px-6 md:px-8 pb-8 border-t border-white/[0.06] pt-6" data-id="element-197">
                        <p className="text-zinc-300 text-sm md:text-base font-light leading-relaxed mb-6 max-w-2xl" data-id="element-198">
                          {pkg.description}
                        </p>
                        <h4 className="text-[10px] font-bold text-zinc-500 tracking-[0.2em] uppercase mb-4" data-id="element-199">
                          What's included
                        </h4>
                        <ul className="space-y-3" data-id="element-200">
                          {pkg.deliverables.map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-201">
                              <CheckIcon className="w-3.5 h-3.5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-202" />
                              {item}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}